import Compressor from 'compressorjs';
import { enqueueSnackbar } from '../snackbar';
import { filesSizeValidator, getFileType } from '../../utils';
import { controlBackdropLoading } from '../../services';
import { INPUT_ERRORS, MESSAGES } from '../../const';

const blobToFile = (blob: Blob, fileName: string) => {
  const file = new File([blob], fileName, {
    type: blob.type,
    lastModified: Date.now(),
  });

  return file;
};

const compressWithRetry = async (
  file: File,
  maxSizeMB = 0.3,
  withoutCompress?: boolean,
): Promise<{ file: File; compressed: boolean }> => {
  let firstSizeMB = file.size / 1024 / 1024;

  if (withoutCompress && file.size <= 9 * 1024 * 1024) {
    return { file, compressed: true };
  }

  if (firstSizeMB <= maxSizeMB && firstSizeMB > 0) {
    return { file, compressed: true };
  }

  const runCompression = (file: File, quality: number): Promise<File> =>
    new Promise((resolve, reject) => {
      new Compressor(file, {
        quality,
        maxWidth: 1920,
        maxHeight: 1920,
        success(result) {
          resolve(blobToFile(result, file.name));
        },
        error(err) {
          reject(err);
        },
      });
    });

  const firstQuality = 0.8;
  let compressedFile = await runCompression(file, firstQuality);
  firstSizeMB = compressedFile.size / 1024 / 1024;

  if (firstSizeMB <= maxSizeMB && firstSizeMB > 0) {
    return { file: compressedFile, compressed: true };
  }

  let targetQuality = firstQuality * (maxSizeMB / firstSizeMB);

  if (targetQuality < 0.1) targetQuality = 0.1;
  if (targetQuality > 0.9) targetQuality = 0.9;

  compressedFile = await runCompression(file, targetQuality);
  const finalSizeMB = compressedFile.size / 1024 / 1024;

  return {
    file: compressedFile,
    compressed: finalSizeMB > 0,
  };
};

export const readFiles = async (
  e: any,
  onReaded: (readedFiles: any[], files: any[]) => void,
  withoutCompress?: boolean,
) => {
  const files = e.target.files;
  let compressedFiles: any[] = [];
  const readedFiles: any = [];
  controlBackdropLoading(1);

  compressedFiles = await Promise.all(
    Object.values(files).map(async (file: any) => {
      const isImage = getFileType(file.name) === 'image';
      if (!isImage) return { file, compressed: false };

      return await compressWithRetry(file, 0.3, withoutCompress);
    }),
  );

  if (compressedFiles.every((item) => item.compressed)) {
    compressedFiles = compressedFiles.map((item) => item.file);

    for (let i = 0; i < compressedFiles.length; i++) {
      const file = compressedFiles[i];

      const reader = new FileReader();
      reader.onload = () => {
        readedFiles[i] = reader.result;

        if (compressedFiles.length - readedFiles.filter(Boolean).length !== 0) return;

        return onReaded && onReaded(readedFiles, compressedFiles);
      };
      reader.readAsDataURL(file);
    }
  } else {
    enqueueSnackbar(MESSAGES.ERROR_BOUNDARY.DEFAULT, 'warning');
  }

  controlBackdropLoading(-1);
};

export const inputFilesValidators = (readedFiles: any[], files: any[]) => {
  if (readedFiles.length > 10) {
    enqueueSnackbar(INPUT_ERRORS.NOT_VALID.MAX_UPLOAD_FILES, 'warning');
    return false;
  }
  if (filesSizeValidator(files)) {
    enqueueSnackbar(filesSizeValidator(files), 'warning');
    return false;
  }

  return true;
};

export const getMultipleValue = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
): string[] => {
  let value: string | string[] = e.target.value;
  value = value.split(',');
  const isNewValue = value.length > 1 ? !value[value.length - 1].trim() : false;
  value = value.map((val) => val.trim()).filter((val) => !!val);
  e.target.value = value.join(',') + (isNewValue ? ',' : '');

  return value;
};
